import { useCallback } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { announceQueued, isQueued } from "@/lib/queuedStart";
import { toast } from "@/lib/toast";
import apiClient from "@/lib/api-client";
import { getAuthToken } from "@/lib/auth";
import { streamSSE } from "@/lib/sse-client";
import { usePdfStore } from "@/stores/pdf-store";
import type {
  PaperListResponse,
  PdfRetrievalStartResponse,
  PdfDownloadUrlResponse,
} from "@/types/paper";

// Statuses the table badges understand; anything else (e.g. "Failed" for an
// upload error) leaves the row alone until the end-of-run refetch.
const ROW_STATUSES = new Set(["Fetching", "Success", "Full Text", "Paywalled", "Unavailable", "Not Available"]);

/** Patch one paper's PDF status in every cached papers page for the concept,
 *  so the table updates as each paper finishes. The refetch on `complete`
 *  still reconciles with the server. */
export function patchPaperPdfStatus(
  queryClient: QueryClient,
  conceptId: string,
  paperId: string,
  status: string,
) {
  if (!ROW_STATUSES.has(status)) return;
  queryClient.setQueriesData<PaperListResponse>(
    { queryKey: ["papers", conceptId] },
    (old) => {
      if (!old?.papers?.some((p) => p.id === paperId)) return old;
      return {
        ...old,
        papers: old.papers.map((p) => {
          if (p.id !== paperId) return p;
          const next = { ...p, canonical_pdf_status: status };
          // A user's own upload stays "user"; a fetched PDF is the shared blob.
          if (status === "Success" && p.pdf_available_source !== "user") {
            next.pdf_available_source = "canonical";
          }
          return next;
        }),
      };
    },
  );
}

interface RetrievalOptions {
  paperIds?: string[];
  force?: boolean;
}

export function usePdfRetrieval(conceptId: string) {
  const queryClient = useQueryClient();

  const startRetrieval = useCallback(
    async (options: RetrievalOptions = {}) => {
      const { startRetrieving, updateProgress, completeRetrieving, failRetrieving } =
        usePdfStore.getState();

      try {
        // Immediately show retrieving state for instant UI feedback — unless
        // this tab is already showing a run (a queued reply mustn't wipe it).
        const showedPending = !usePdfStore.getState().isRetrieving;
        if (showedPending) startRetrieving("pending", options.paperIds?.length ?? 0);

        // POST to start retrieval job
        const body: Record<string, unknown> = {};
        if (options.paperIds) body.paper_ids = options.paperIds;
        if (options.force) body.force = true;

        const reply = await apiClient
          .post(`api/concepts/${conceptId}/pdf/retrieve`, { json: body })
          .json<PdfRetrievalStartResponse | unknown>();
        // Already running: the server holds this start until it finishes.
        if (isQueued(reply)) {
          if (showedPending) usePdfStore.getState().resetRetrieving();
          announceQueued(queryClient, conceptId, "PDF retrieval", reply);
          return;
        }
        const res = reply as PdfRetrievalStartResponse;

        // Update with real job ID and total from server
        startRetrieving(res.job_id, res.total_papers);

        // Open SSE stream
        const baseUrl =
          import.meta.env.VITE_API_URL ?? "http://localhost:8000";
        const token = await getAuthToken();
        const streamUrl = `${baseUrl}/api/concepts/${conceptId}/pdf/${res.job_id}/stream`;

        const controller = new AbortController();

        let receivedTerminal = false;

        for await (const event of streamSSE(
          streamUrl,
          { Authorization: `Bearer ${token}` },
          controller.signal,
        )) {
          if (event.event === "job_started") {
            const data = JSON.parse(event.data);
            updateProgress({
              total: data.total,
              statusMessage: `Retrieving PDFs: 0 of ${data.total}`,
            });
          } else if (event.event === "paper_start") {
            const data = JSON.parse(event.data);
            updateProgress({ currentPaper: data.title });
            patchPaperPdfStatus(queryClient, conceptId, data.paper_id, "Fetching");
          } else if (event.event === "paper_complete") {
            const data = JSON.parse(event.data);
            patchPaperPdfStatus(queryClient, conceptId, data.paper_id, data.status);
            const { progress } = usePdfStore.getState();
            const isSuccess = data.status === "Success" || data.status === "Full Text";
            const succeeded = isSuccess
              ? progress.succeeded + 1
              : progress.succeeded;
            const failed = !isSuccess
              ? progress.failed + 1
              : progress.failed;
            const completed = succeeded + failed;
            const percent = Math.round((completed / progress.total) * 100);
            updateProgress({
              completed,
              succeeded,
              failed,
              percent,
              statusMessage: `Retrieving PDFs: ${completed} of ${progress.total} (${succeeded} success, ${failed} not available)`,
            });
          } else if (event.event === "complete") {
            const data = JSON.parse(event.data);
            const parts: string[] = [];
            if (data.succeeded > 0) parts.push(`${data.succeeded} retrieved`);
            if (data.failed > 0) parts.push(`${data.failed} not available`);
            const msg = parts.length > 0 ? parts.join(", ") : "No PDFs to retrieve";
            completeRetrieving(msg);
            if (data.succeeded > 0 && data.failed === 0) {
              toast.success("PDF retrieval complete", { description: msg });
            } else if (data.succeeded > 0) {
              toast.info("PDF retrieval complete", { description: msg });
            } else {
              toast.warning("No PDFs retrieved", { description: msg });
            }
            receivedTerminal = true;
            await queryClient.invalidateQueries({
              queryKey: ["papers", conceptId],
            });
            return;
          } else if (event.event === "error") {
            const data = JSON.parse(event.data);
            const errMsg = data.error || "PDF retrieval failed";
            failRetrieving(errMsg);
            toast.error("PDF retrieval failed", { description: errMsg });
            receivedTerminal = true;
            return;
          } else if (event.event === "cancelled") {
            failRetrieving("PDF retrieval cancelled");
            toast.info("PDF retrieval cancelled");
            receivedTerminal = true;
            return;
          }
        }

        // Stream closed without terminal event — refresh table as fallback
        if (!receivedTerminal) {
          completeRetrieving("PDF retrieval finished");
          toast.success("PDF retrieval complete");
          await queryClient.invalidateQueries({
            queryKey: ["papers", conceptId],
          });
        }
      } catch (err) {
        const { failRetrieving } = usePdfStore.getState();
        const msg = err instanceof Error ? err.message : "PDF retrieval failed";
        failRetrieving(msg);
        toast.error("PDF retrieval error", { description: msg });
      }
    },
    [conceptId, queryClient],
  );

  const cancelRetrieval = useCallback(async () => {
    const { jobId } = usePdfStore.getState();
    if (!jobId) return;

    try {
      await apiClient.post(
        `api/concepts/${conceptId}/pdf/${jobId}/cancel`,
      );
      usePdfStore.getState().failRetrieving("Cancelled by user");
    } catch {
      // Best-effort cancel
    }
  }, [conceptId]);

  const getDownloadUrl = useCallback(
    async (
      paperId: string,
    ): Promise<{ url: string; source: "user" | "canonical" | "none" } | null> => {
      try {
        const res = await apiClient
          .get(`api/concepts/${conceptId}/pdf/download-url/${paperId}`)
          .json<PdfDownloadUrlResponse>();
        return { url: res.download_url, source: res.source ?? "none" };
      } catch {
        // A 404 here can mean the backend just self-healed a phantom
        // canonical blob (object missing in R2 → status reset to Idle).
        // Refresh the papers cache so the stale "Success" flips to the
        // re-fetch CTA instead of leaving a dead "View PDF" affordance.
        await queryClient.invalidateQueries({
          queryKey: ["papers", conceptId],
        });
        return null;
      }
    },
    [conceptId, queryClient],
  );

  const extractAbstract = useCallback(
    async (paperId: string): Promise<{ abstract: string | null; source: string } | null> => {
      try {
        const res = await apiClient
          .post(
            `api/concepts/${conceptId}/pdf/extract-abstract/${paperId}`,
          )
          .json<{ abstract: string | null; source: string }>();

        await queryClient.invalidateQueries({
          queryKey: ["papers", conceptId],
        });

        if (res.abstract) {
          toast.success("Abstract found", {
            description: `Source: ${res.source}`,
          });
        } else {
          toast.warning("Abstract not available", {
            description:
              "Could not extract abstract from APIs or full text.",
          });
        }
        return res;
      } catch {
        toast.error("Abstract extraction failed", {
          description: "An error occurred. Please try again.",
        });
        return null;
      }
    },
    [conceptId, queryClient],
  );

  return { startRetrieval, cancelRetrieval, getDownloadUrl, extractAbstract };
}
